import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canManageOps, roleLabel } from "@/lib/auth";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Users, Mail, Trash2 } from "lucide-react";

export default function Employees({ embedded = false }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const manage = canManageOps(user);

  const { data: employees } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", name: "", password: "", role: "employee",
    position: "", department: "", shift: "Day", phone: "",
    photo_url: "https://images.pexels.com/photos/26872232/pexels-photo-26872232.jpeg",
  });

  const submit = async () => {
    try {
      await api.post("/employees", form);
      toast.success(`${form.name} added`);
      qc.invalidateQueries({ queryKey: ["employees"] });
      setOpen(false);
      setForm({ ...form, email: "", name: "", password: "", position: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this user?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Deactivated");
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const badgeVariant = (r) => ({
    company_admin: "default",
    supervisor: "secondary",
    employee: "outline",
    platform_admin: "destructive",
  }[r] || "outline");

  return (
    <div className={embedded ? "" : "p-4 md:p-8 max-w-[1400px]"}>
      <div className="flex items-start justify-between mb-8">
        <div>
          {!embedded && (
            <>
              <div className="label-caps text-muted-foreground mb-2">Configure</div>
              <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Employees</h1>
              <p className="text-sm text-muted-foreground mt-2">Team members, roles and access.</p>
            </>
          )}
          {embedded && (
            <p className="text-sm text-muted-foreground">Everyone in your tenant — add staff, edit roles, or deactivate.</p>
          )}
        </div>
        {manage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-btn"><UserPlus className="w-4 h-4 mr-1" />Add Employee</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Full name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" /></div>
                <div><Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" /></div>
                <div><Label>Password</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="emp-password" /></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(user?.role === "platform_admin"
                        ? ["company_admin","supervisor","employee"]
                        : ["supervisor","employee","company_admin"]).map((r) => (
                        <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Shift</Label>
                  <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Day","Morning","Evening","Night","Weekend"].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Position</Label>
                  <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
                <div><Label>Department</Label>
                  <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                <div className="col-span-2"><Label>Photo URL</Label>
                  <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={submit} data-testid="emp-submit">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {employees?.map((e) => (
          <div key={e.id} className="border border-border bg-card rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid={`employee-${e.id}`}>
            <div className="flex items-start gap-4">
              <Avatar className="w-14 h-14 border border-border">
                <AvatarImage src={e.photo_url} />
                <AvatarFallback>{e.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm truncate">{e.name}</div>
                  <Badge variant={badgeVariant(e.role)} className="shrink-0 text-[0.65rem]">{roleLabel(e.role)}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{e.position || "—"}</div>
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1 truncate">
                  <Mail className="w-3 h-3" /> {e.email}
                </div>
                <div className="flex items-center gap-2 mt-3 text-xs">
                  {e.department && <span className="px-2 py-0.5 bg-muted rounded-sm">{e.department}</span>}
                  {e.shift && <span className="px-2 py-0.5 bg-muted rounded-sm">{e.shift}</span>}
                  {e.employment_status !== "active" && <Badge variant="destructive" className="text-[0.65rem]">Inactive</Badge>}
                </div>
              </div>
            </div>
            {manage && e.id !== user.id && e.employment_status === "active" && (
              <div className="mt-4 pt-4 border-t border-border flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => deactivate(e.id)}>
                  <Trash2 className="w-3.5 h-3.5 mr-1 text-destructive" />Deactivate
                </Button>
              </div>
            )}
          </div>
        ))}
        {employees?.length === 0 && (
          <div className="col-span-full border border-dashed border-border p-12 text-center">
            <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <div className="font-medium">No employees</div>
          </div>
        )}
      </div>
    </div>
  );
}
